import {
  log,
  generateTraceId,
  createServerLogger,
  type LogExtras,
} from '../services/logger.service'

/**
 * Universal logger composable that works on both server and client
 *
 * On server: Uses the trace ID from the request context (set by 00.logger middleware)
 * On client: Uses a session-based trace ID for client-side only logging
 *
 * For server middleware and API routes, prefer using createServerLogger(event) directly
 */
export const useLogger = () => {
  // Get or create trace ID based on environment
  const getTraceId = (): string => {
    if (process.server) {
      // On server: try to get trace ID from SSR context
      try {
        // @ts-ignore - useNuxtApp is auto-imported by Nuxt
        const nuxtApp = useNuxtApp?.()
        const traceId = nuxtApp?.ssrContext?.event?.context?.traceId
        if (traceId) return traceId
      } catch (e) {
        // If useNuxtApp fails (e.g., outside of Nuxt context), generate new ID
      }
      // Fallback: generate new trace ID
      return generateTraceId()
    } else {
      // On client: use a session-based trace ID
      if (!window.__CLIENT_TRACE_ID__) {
        window.__CLIENT_TRACE_ID__ = generateTraceId()
      }
      return window.__CLIENT_TRACE_ID__
    }
  }

  const traceId = getTraceId()

  return {
    logger: {
      error: (message: unknown, extras?: LogExtras) => log.error(message, extras, traceId),
      warn: (message: unknown, extras?: LogExtras) => log.warn(message, extras, traceId),
      info: (message: unknown, extras?: LogExtras) => log.info(message, extras, traceId),
      debug: (message: unknown, extras?: LogExtras) => log.debug(message, extras, traceId),
    },
    traceId,
  }
}

// Extend window for client-side trace ID storage
declare global {
  interface Window {
    __CLIENT_TRACE_ID__?: string
  }
}

/**
 * Server-specific logger for use in server API routes and middleware
 * This directly uses the event context for trace ID
 *
 * @example
 * // In server/api/example.ts
 * export default defineEventHandler(async (event) => {
 *   const logger = createServerLogger(event)
 *   logger.info('Processing API request')
 *   // ... rest of handler
 * })
 */
export { createServerLogger } from '../services/logger.service'
